// RigSentinel — Daily Operations Module Foundation (Phase RCE-2)
//
// SHARED SCHEMA
// -------------
// Every Daily Operations document is a `kind`-tagged record with a common
// envelope (identity, context snapshot, lifecycle) plus a per-kind `payload`
// block where the kind-specific form data lives.
//
// CONTEXT FREEZE / AUDIT-TRAIL invariant (RCE-2 approval):
//   * On first save, `contextSnapshot.frozen = true` and the resolved
//     context (operator/well/depth/...) is copied INTO the document.
//   * Subsequent edits NEVER auto-mutate the snapshot. The user must press
//     "↻ Re-pull from Context" to refresh.
//   * `contextVersion` carries the engine version that produced the
//     snapshot — currently "RCE-1.0".

import { CONTEXT_VERSION, DAILY_OPS_KINDS, isKnownKind, isKindEnabled } from "./kinds";
import { resolve } from "@/lib/rce/resolver";
import { setOverride } from "@/lib/rce/overrides";
import { stampEngineeringMetadata } from "@/lib/rce/snapshot";

export const DAILY_OPS_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Empty / default factories
// ---------------------------------------------------------------------------

/** Empty per-kind payload. Kinds register a payload factory by id; unknown
 *  kinds get an empty object. */
const PAYLOAD_FACTORIES = {
  ddr: () => ({
    header: {
      reportDate: "", reportNumber: "", tour: "",
      preparedBy: "", position: "",
      periodStart: "", periodEnd: "", timeZone: "",
      unitSystem: "imperial",
    },
    well: {
      // Kept for the rare case the user enters them manually before a
      // session is set. Resolver values flow into a parallel `contextSnapshot`
      // — they DO NOT clobber what the user types here.
      wellType: "", wellStatus: "",
      previousDepth: "", footageDrilled: "",
      rkbElevation: "", mudlineRef: "",
      casingSize: "",
      formation: "", targetDepth: "", nextCasingPoint: "",
    },
    ops: {
      mainOperation: "", notes: "",
      accomplishments: "", problems: "",
      correctiveActions: "", currentStatus: "", nextPlan: "",
    },
    activities: [],
    params: {}, mud: {}, bha: {}, equipment: {},
    bop: {}, hse: {}, pob: {}, logistics: {},
    weather: {}, npt: {},
  }),
  shiftReport:    () => ({ summary: "" }),
  tourReport:     () => ({ summary: "" }),
  dailyOpsLog:    () => ({ entries: [] }),
  supervisorNotes:() => ({ notes: "" }),
  endOfDayReport: () => ({ summary: "" }),
};

/** A bare context snapshot — every field defaults to "". The resolver
 *  populates real values via `freezeContextSnapshot` on first save and on
 *  explicit re-pull. */
export function emptyContextSnapshot() {
  return {
    frozen: false,
    // Engineering Snapshot ID (RCE-4 refinement) — stamped on first freeze.
    snapshotId: "",
    contextVersion: CONTEXT_VERSION,
    capturedAt: null,
    // Mirror of fields the kind consumes.
    project: { operator: "", client: "", country: "", field: "" },
    rig:     { name: "", contractor: "" },
    well:    {
      name: "", environment: "", waterDepthFt: "",
      holeSection: "", holeSizeIn: "",
      currentMdFt: "", currentTvdFt: "",
      casingShoeMdFt: "", casingShoeTvdFt: "",
    },
    bha:     { id: "", revision: "" },
    drillString: { id: "", revision: "" },
    mud:     { weightInPpg: "" },
    operation: { current: "" },
    // Records the RCE confidence of every value at freeze time. The Context
    // Inspector reads this when reviewing a frozen document.
    confidence: {},
    // Records which session context was active at freeze time.
    sessionContextIds: {
      projectId: "", rigId: "", wellId: "", bhaId: "", drillStringId: "",
    },
    // Master modules that contributed data (RCE-4 refinement).
    sourceModules: [],
  };
}

/** Empty Daily Operations document of the given kind. */
export function emptyDailyOpsDocument(kind = "ddr") {
  if (!isKnownKind(kind))
    throw new Error(`Unknown Daily Operations kind: ${kind}`);
  const payloadFactory = PAYLOAD_FACTORIES[kind] || (() => ({}));
  return {
    id: "",
    kind,
    schemaVersion: DAILY_OPS_SCHEMA_VERSION,
    contextVersion: CONTEXT_VERSION,
    // Lineage / identity
    wellId:        "",
    projectId:     "",
    rigId:         "",
    bhaId:         "",
    drillStringId: "",
    date:  "",
    shift: "",
    status: "draft",            // draft → final → archived
    contextSnapshot: emptyContextSnapshot(),
    payload: payloadFactory(),
    notes: "",
    isActive: true,
    createdAt: null,
    updatedAt: null,
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const STATUSES = ["draft", "final", "archived"];

export function validateDailyOpsDocument(rec) {
  if (!rec || typeof rec !== "object")
    return { ok: false, errors: ["Document is missing."] };
  const errs = [];
  if (!isKnownKind(rec.kind))
    errs.push(`Unknown Daily Operations kind: ${rec.kind}.`);
  if (!isKindEnabled(rec.kind))
    errs.push(`Document kind "${rec.kind}" is reserved but not yet enabled.`);
  if (!STATUSES.includes(rec.status))
    errs.push(`Status must be one of: ${STATUSES.join(", ")}.`);
  if (rec.contextVersion && typeof rec.contextVersion !== "string")
    errs.push("contextVersion must be a string.");
  return { ok: errs.length === 0, errors: errs };
}

// ---------------------------------------------------------------------------
// Context freeze / re-pull
// ---------------------------------------------------------------------------

/**
 * Resolve every consumed field from the RCE for the given ownership
 * declaration and return a fresh `contextSnapshot`. Pure — does NOT touch
 * storage and does NOT mutate the input record.
 */
export function buildContextSnapshot(kind, ctx) {
  const k = DAILY_OPS_KINDS[kind];
  if (!k) throw new Error(`Unknown kind: ${kind}`);
  const snap = emptyContextSnapshot();
  snap.frozen = true;
  snap.contextVersion = CONTEXT_VERSION;
  snap.capturedAt = new Date().toISOString();
  snap.sessionContextIds = {
    projectId:     ctx?.projectId     || "",
    rigId:         ctx?.rigId         || "",
    wellId:        ctx?.wellId        || "",
    bhaId:         ctx?.bhaId         || "",
    drillStringId: ctx?.drillStringId || "",
  };
  for (const path of k.ownership.consumes) {
    const r = resolve(path, ctx || {});
    const value = (r.confidence === "unknown" || r.confidence === "conflict")
      ? "" : r.value;
    _assignDottedPath(snap, _localKey(path), value);
    snap.confidence[path] = r.confidence;
  }
  // RCE-4 — stamp the permanent Engineering Snapshot ID + Source Modules.
  // Daily Operations documents always include the Daily Operations source.
  stampEngineeringMetadata(snap, { includeDailyOps: true });
  return snap;
}

// `project.operator` → "project.operator" → snap.project.operator
function _localKey(path) {
  return path;
}

function _assignDottedPath(target, dotted, value) {
  const parts = dotted.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
}

/** Freeze (or refresh) a document's contextSnapshot using the current
 *  resolver state. Returns the patched record (DOES NOT persist). */
export function freezeContextSnapshot(rec, ctx) {
  const snap = buildContextSnapshot(rec.kind, ctx);
  return { ...rec, contextSnapshot: snap, contextVersion: snap.contextVersion };
}

/**
 * Apply a document-scope override to the RCE for one of the kind's
 * permitted override paths. Refuses any path NOT in the kind's
 * `ownership.overrides` whitelist.
 */
export function overrideContextField(rec, path, value, reason = "") {
  const k = DAILY_OPS_KINDS[rec.kind];
  if (!k) throw new Error(`Unknown kind: ${rec.kind}`);
  if (!k.ownership.overrides.includes(path))
    throw new Error(`Document kind "${rec.kind}" cannot override field "${path}".`);
  if (!rec.id) throw new Error("Document must be persisted before overrides can be set.");
  return setOverride({
    scope: "document", scopeId: rec.id, fieldPath: path, value, reason,
  });
}
